import { useEffect, useState } from 'react';
import Dialogo from '@/components/Dialogo';
import { useConversas } from '@/conversas/ConversasProvider';
import { simularMensagemRecebida } from '@/services/conversasService';
import { usandoEmuladores } from '@/lib/firebase';
// Mesmo mapa que o "Iniciar conversa" usa: duas cópias da regra
// canal→papel do número iam divergir na primeira mudança.
import { papelDoNumeroDoCanal } from '@/conversas/canais';

const PAPEIS = [
  {
    id: 'marketing_entrada',
    label: 'Marketing — entrada (oficial)',
    dica: 'Cria lead novo e roteia para Vendas. É a única porta de entrada de campanha.',
  },
  {
    id: 'marketing_resgate',
    label: 'Evolution (marketing)',
    dica: 'Vai para Vendas. Só casa com lead já existente; não cria lead de campanha.',
  },
  {
    id: 'recepcao',
    label: 'Recepção (Evolution)',
    dica: 'Vai para o espaço Recepção.',
  },
];

/**
 * Injeta uma mensagem como se tivesse chegado do WhatsApp, pelo mesmo caminho
 * dos webhooks reais. Só aparece em desenvolvimento — a Function recusa rodar
 * fora do emulador.
 */
export default function SimuladorMensagem({ aberto, aoFechar }) {
  const { clinicaId, selecionar, canal } = useConversas();
  const [papel, setPapel] = useState(() => papelDoNumeroDoCanal(canal));

  /**
   * O canal aberto define o papel sugerido.
   *
   * Sem isto, simular a partir do canal de Resgate injetaria uma mensagem de
   * `marketing_entrada`, que cai no canal de Leads — a conversa nasceria fora
   * da lista que está na tela, e pareceria que o filtro por canal quebrou.
   * Continua trocável no seletor: dá para injetar em outro canal de propósito.
   */
  useEffect(() => {
    if (canal) setPapel(papelDoNumeroDoCanal(canal));
  }, [canal]);
  const [telefone, setTelefone] = useState('(51) 98888-1234');
  const [nome, setNome] = useState('Contato de Teste');
  const [texto, setTexto] = useState('Oi, vi o anúncio e queria saber sobre implante.');
  const [comAnuncio, setComAnuncio] = useState(true);
  const [erro, setErro] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [enviando, setEnviando] = useState(false);

  const escolhido = PAPEIS.find((p) => p.id === papel);

  async function injetar(evento) {
    evento.preventDefault();
    setErro(null);
    setResultado(null);
    setEnviando(true);
    try {
      const { data } = await simularMensagemRecebida({
        clinicaId,
        papel,
        telefone,
        nome,
        texto,
        referral:
          comAnuncio && papel === 'marketing_entrada'
            ? {
                source_id: 'ad-simulado-001',
                source_type: 'ad',
                headline: 'Implante com garantia de 5 anos',
                ctwa_clid: `clid-${Date.now()}`,
              }
            : null,
      });
      setResultado(data);
      selecionar(data.conversaId);
    } catch (e) {
      setErro(e.message ?? 'Não foi possível simular a mensagem.');
    } finally {
      setEnviando(false);
    }
  }

  if (!usandoEmuladores) return null;

  return (
    <Dialogo titulo="Simular mensagem recebida" aberto={aberto} aoFechar={aoFechar} largura={520}>
      <form className="formulario" onSubmit={injetar}>
        <p className="dialogo-contexto">
          A mensagem entra pelo mesmo caminho dos webhooks reais: normalização →
          conversa → mensagem. Serve para exercitar o inbox sem VPS e sem número oficial.
        </p>

        <div className="campo">
          <label htmlFor="sim-papel">Canal de entrada</label>
          <select id="sim-papel" value={papel} onChange={(e) => setPapel(e.target.value)}>
            {PAPEIS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
          <span className="indicador-nota">{escolhido?.dica}</span>
        </div>

        <div className="linha">
          <div className="campo">
            <label htmlFor="sim-telefone">Telefone</label>
            <input
              id="sim-telefone"
              required
              value={telefone}
              onChange={(e) => setTelefone(e.target.value)}
            />
          </div>
          <div className="campo">
            <label htmlFor="sim-nome">Nome no WhatsApp</label>
            <input id="sim-nome" value={nome} onChange={(e) => setNome(e.target.value)} />
          </div>
        </div>

        <div className="campo">
          <label htmlFor="sim-texto">Mensagem</label>
          <textarea
            id="sim-texto"
            rows={3}
            required
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
        </div>

        {papel === 'marketing_entrada' && (
          <label className="filtro-check">
            <input
              type="checkbox"
              checked={comAnuncio}
              onChange={(e) => setComAnuncio(e.target.checked)}
            />
            Simular clique em anúncio (gera origem meta_ctwa no lead)
          </label>
        )}

        {erro && <p className="erro">{erro}</p>}

        {resultado && (
          <p className="aviso">
            Mensagem entregue. {resultado.conversaNova ? 'Conversa nova criada' : 'Conversa existente atualizada'}
            {resultado.saudacaoEnviada ? ', saudação automática disparada' : ''}.
          </p>
        )}

        <footer className="dialogo-acoes">
          <button type="button" className="btn-secundario" onClick={aoFechar}>
            Fechar
          </button>
          <button type="submit" className="btn-primario" disabled={enviando}>
            {enviando ? 'Injetando…' : 'Injetar mensagem'}
          </button>
        </footer>
      </form>
    </Dialogo>
  );
}
